import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import getDistance from "geolib/es/getDistance";
import math from "mathjs";
import { getLeafRating } from "../../../util/leafRating";
import { getDisplayableMenuItems } from "../../shared/MenuDisplay";
import LeafRating from "../../common/LeafRating";
import RestaurantActionMenu from "../restaurantResults/RestaurantActionMenu";
import { slugify } from "../../../util/slugify";
import useEvent from "../../../hooks/useEvent";
import HighlightText from "../../shared/HighlightText";
import "./CompactCard.css";

const CompactCard = ({
  restaurant,
  currentLocation,
  isSelected,
  isExpanded,
  onClick,
  token,
  searchQuery,
}) => {
  const { event } = useEvent();

  // Calculate distance
  const distanceInMeters = getDistance(
    currentLocation,
    restaurant.geoJson.coordinates
  );
  const distance = math.unit(distanceInMeters, "m").to("mi");
  const formattedDistance = math.format(distance, { notation: "fixed", precision: 1 });

  // Get leaf rating
  const rating = getLeafRating(restaurant);

  // Get cuisines
  const cuisines = restaurant.cuisine?.filter(Boolean).map(c =>
    c.replace(/\b\w/g, l => l.toUpperCase())
  ) || [];

  // Get menu items (always compute for animation - content is always in DOM)
  const menuItems = getDisplayableMenuItems(restaurant);

  return (
    <div
      className={`compact-card ${isSelected ? "compact-card-selected" : ""} ${isExpanded ? "compact-card-expanded" : ""}`}
      onClick={onClick}
    >
      {/* Main row - always visible */}
      <div className="compact-card-main">
        <div className="compact-card-info">
          <div className="compact-card-name">
            {restaurant.metadata?.adHocSearch && <span className="compact-card-sparkle">Discovery </span>}
            {restaurant.name}
          </div>
          <div className="compact-card-meta">
            {!event && (
              <>
                <LeafRating count={rating.count} tier={rating.tier} />
                <span className="compact-card-dot">·</span>
              </>
            )}
            <span className="compact-card-distance">{formattedDistance}</span>
            {cuisines.length > 0 && (
              <>
                <span className="compact-card-dot">·</span>
                <span className="compact-card-cuisines-inline">{cuisines.join(", ")}</span>
              </>
            )}
          </div>
        </div>
        {token && (
          <div className="compact-card-action-menu" onClick={(e) => e.stopPropagation()}>
            <RestaurantActionMenu
              restaurant={restaurant}
              token={token}
            />
          </div>
        )}
        <div className="compact-card-chevron">
          <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`} />
        </div>
      </div>

      {/* Expandable content wrapper - always rendered for animation */}
      <div className="compact-card-expandable">
        <div className="compact-card-expandable-inner">
          <div className="compact-card-expanded-content">
            {/* Cuisines */}
            {cuisines.length > 0 && (
              <div className="compact-card-cuisines">
                {cuisines.map((cuisine, i) => (
                  <span key={i} className="compact-card-cuisine-tag">{cuisine}</span>
                ))}
              </div>
            )}

            {/* Menu items - horizontal scroll */}
            {menuItems.length > 0 && (
              <div className="compact-card-menu">
                <div className="compact-card-menu-header">
                  <span className="compact-card-menu-title">Menu Options</span>
                  <span className="compact-card-menu-count">{menuItems.length}</span>
                </div>
                <div className="compact-card-menu-scroll">
                  {menuItems.map((item, index) => {
                    const sectionName = item.section?.name || (typeof item.section === 'string' ? item.section : null);
                    return (
                      <div key={item._id || index} className="compact-card-menu-item">
                        {sectionName && (
                          <span className="compact-card-menu-item-section">{sectionName}</span>
                        )}
                        <span className="compact-card-menu-item-name">
                          <HighlightText text={item.option} query={searchQuery} />
                        </span>
                        {item.description && (
                          <span className="compact-card-menu-item-desc">
                            <HighlightText text={item.description} query={searchQuery} />
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Action Link - different for ad hoc vs regular restaurants */}
            {restaurant.metadata?.adHocSearch ? (
              <Link
                to="/create/v2/cuisine"
                state={{
                  restaurant: restaurant,
                  fromAdHoc: true,
                }}
                className="compact-card-details-link compact-card-creator-link"
                onClick={(e) => e.stopPropagation()}
              >
                <i className="fas fa-edit" />
                Open in Creator
              </Link>
            ) : (
              <Link
                to={`/details/${restaurant._id}/${slugify(restaurant.name)}${searchQuery ? `?keywords=${encodeURIComponent(searchQuery)}` : ""}`}
                className="compact-card-details-link"
                onClick={(e) => e.stopPropagation()}
              >
                View Full Details
                <i className="fas fa-arrow-right" />
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

CompactCard.propTypes = {
  restaurant: PropTypes.object.isRequired,
  currentLocation: PropTypes.object.isRequired,
  isSelected: PropTypes.bool,
  isExpanded: PropTypes.bool,
  onClick: PropTypes.func,
  token: PropTypes.string,
};

const mapStateToProps = (state) => ({
  token: state.auth.token,
});

export default connect(mapStateToProps)(CompactCard);
